import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, Trophy, RefreshCw } from 'lucide-react';

/**
 * iter305 — Admin queue for Seller Success Stories.
 * Approve → +10 coins credited to the seller atomically.
 * Reject  → No coins, audit log only.
 */
export default function AdminSellerStories() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('submitted');
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/seller-stories?status=${filter}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) setStories(data.stories || []);
      else toast.error(data.detail || 'Failed to load');
    } catch (e) {
      console.error(e);
      toast.error(isRTL ? 'خطأ في تحميل القصص' : 'Failed to load stories');
    } finally {
      setLoading(false);
    }
  }, [token, filter, isRTL]);

  useEffect(() => { load(); }, [load]);

  // iter305 — keep `act` independent of the `load` reference so the
  // linter doesn't transitively flag it as effect-bound.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  const act = useCallback(async (storyId, action) => {
    setActingId(storyId);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/seller-stories/${storyId}/${action}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (data.idempotent_replay) {
          toast.info(isRTL ? 'سبق وتم اتخاذ الإجراء' : 'Already actioned');
        } else if (action === 'approve') {
          toast.success(isRTL
            ? `تمت الموافقة وإضافة ${data.reward} عملات للبائع`
            : `Approved — +${data.reward} coins credited to seller`);
        } else {
          toast.success(isRTL ? 'تم الرفض' : 'Rejected');
        }
        loadRef.current?.();
      } else {
        toast.error(data.detail || (isRTL ? 'فشل الإجراء' : 'Action failed'));
      }
    } catch (e) {
      console.error(e);
      toast.error(isRTL ? 'خطأ في الشبكة' : 'Network error');
    } finally {
      setActingId(null);
    }
  }, [token, isRTL]);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="admin-seller-stories">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 grid place-items-center">
            <Trophy size={20} />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-slate-800">
              {isRTL ? 'قصص نجاح البائعين' : 'Seller Success Stories'}
            </h1>
            <p className="text-xs text-slate-500">
              {isRTL
                ? 'الموافقة تُضيف +10 عملات للبائع تلقائيًا'
                : 'Approval atomically credits +10 coins to the seller'}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600"
          aria-label="reload"
          data-testid="admin-seller-stories-reload"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="admin-seller-stories-filters">
        {[
          { v: 'submitted', en: 'Submitted', ar: 'قيد المراجعة' },
          { v: 'approved',  en: 'Approved',  ar: 'موافق عليها' },
          { v: 'rejected',  en: 'Rejected',  ar: 'مرفوضة' },
          { v: 'all',       en: 'All',       ar: 'الكل' },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`admin-seller-stories-filter-${f.v}`}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              filter === f.v
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {isRTL ? f.ar : f.en}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-12 text-slate-400" data-testid="admin-seller-stories-loading">
          <Loader2 size={28} className="animate-spin" />
        </div>
      ) : stories.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/5 p-8 text-center text-slate-500" data-testid="admin-seller-stories-empty">
          {isRTL ? 'لا توجد قصص في هذه الحالة' : 'No stories in this filter'}
        </div>
      ) : (
        <ul className="space-y-3">
          {stories.map((s) => (
            <li
              key={s.story_id}
              className="bg-white rounded-2xl border border-black/5 p-4 shadow-sm"
              data-testid={`admin-seller-story-${s.story_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <h3 className="text-sm md:text-base font-semibold text-slate-800 truncate">
                    {s.title}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {s.user_name || s.user_email || s.user_id}
                    {' · '}
                    {new Date(s.submitted_at || s.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${
                  s.status === 'approved' ? 'bg-emerald-50 text-emerald-700'
                  : s.status === 'rejected' ? 'bg-rose-50 text-rose-700'
                  : 'bg-amber-50 text-amber-700'
                }`}>
                  {s.status}
                </span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                {s.body}
              </p>
              {s.status === 'submitted' && (
                <div className="flex gap-2 mt-3 justify-end">
                  <button
                    onClick={() => act(s.story_id, 'reject')}
                    disabled={actingId === s.story_id}
                    data-testid={`reject-${s.story_id}`}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-rose-100 hover:bg-rose-200 text-rose-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    <XCircle size={14} />
                    {isRTL ? 'رفض' : 'Reject'}
                  </button>
                  <button
                    onClick={() => act(s.story_id, 'approve')}
                    disabled={actingId === s.story_id}
                    data-testid={`approve-${s.story_id}`}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60 flex items-center gap-1"
                  >
                    <CheckCircle2 size={14} />
                    {isRTL ? 'موافقة (+10)' : 'Approve (+10)'}
                  </button>
                </div>
              )}
              {s.status === 'approved' && s.reward_paid && (
                <p className="text-xs text-emerald-600 mt-2">
                  {isRTL ? '✓ تم إضافة +10 عملات للبائع' : '✓ +10 coins credited to seller'}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
